"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { db } from "@/lib/db";
import type { AiProfile } from "@/types/ai";
import type { Project } from "@/types/project";
import { Plus, FolderKanban } from "lucide-react";
import { getTextColorForBackground, getSecondaryTextColorForBackground, getTextColorWithOpacity } from "@/lib/utils/colorContrast";
import BrandStyles from "@/components/branding/BrandStyles";
import PremiumNavigation from "@/components/layout/PremiumNavigation";

interface Props {
  profile: AiProfile;
}

export default function ProjectsList({ profile }: Props) {
  const router = useRouter();
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    loadProjects();
  }, [profile.id]);

  async function loadProjects() {
    const projs = await db.projects
      .where("profileId")
      .equals(profile.id)
      .reverse()
      .sortBy("updatedAt");
    setProjects(projs);
  }

  // Get gradient colors based on brand - same as chat interface
  const getGradientColors = () => {
    const primary = profile.brand?.primaryColor || "#87CEEB";
    const secondary = profile.brand?.secondaryColor || "#B0E0E6";
    return { primary, secondary };
  };

  const gradientColors = getGradientColors();
  const primaryColor = gradientColors.primary;
  const secondaryColor = gradientColors.secondary;

  // Calculate theme-aware text colors for white glass backgrounds
  const inputBackgroundColor = "#ffffff";
  const textColor = profile.brand?.textColor || getTextColorForBackground(inputBackgroundColor);
  const textSecondaryColor = profile.brand?.textSecondaryColor || getSecondaryTextColorForBackground(inputBackgroundColor);
  const textMutedColor = getTextColorWithOpacity(inputBackgroundColor, 0.6);
  const textVeryMutedColor = getTextColorWithOpacity(inputBackgroundColor, 0.4);

  return (
    <div 
      className="flex flex-col h-screen relative overflow-hidden"
      style={{
        // Animated gradient that shifts and moves - same as chat interface
        background: `linear-gradient(135deg, ${gradientColors.primary} 0%, ${gradientColors.secondary} 25%, ${gradientColors.primary} 50%, ${gradientColors.secondary} 75%, ${gradientColors.primary} 100%)`,
        backgroundSize: "400% 400%",
        animation: "gradientShift 15s ease infinite",
        minHeight: '-webkit-fill-available', // iOS Safari fix
      }}
    >
      <BrandStyles brand={profile.brand} />

      {/* Enhanced animated background - same as chat interface */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div 
          className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"
          style={{ backgroundColor: gradientColors.primary }}
        />
        <div 
          className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"
          style={{ backgroundColor: gradientColors.secondary }}
        />
        {/* Subtle overlay for depth */}
        <div 
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at 30% 50%, ${gradientColors.primary}15 0%, transparent 50%), radial-gradient(circle at 70% 50%, ${gradientColors.secondary}15 0%, transparent 50%)`
          }}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto relative z-10 overscroll-contain">
        <div style={{ maxWidth: '48rem', margin: '0 auto', padding: '1.5rem 1rem 2rem', minHeight: '100%' }}>
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{ color: textColor }}
              >
                Projects
              </h1>
              <p 
                className="text-base md:text-lg"
                style={{ color: textSecondaryColor }}
              >
                Scoped workspaces for focused thinking
              </p>
            </div>
            <button
              onClick={() => router.push("/projects/new")}
              className="px-4 py-2.5 md:px-6 md:py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 flex items-center gap-2 flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                color: getTextColorForBackground(primaryColor),
                boxShadow: `0 4px 16px ${primaryColor}40`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 6px 24px ${primaryColor}60`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = `0 4px 16px ${primaryColor}40`;
              }}
            >
              <Plus className="w-4 h-4 md:w-5 md:h-5" />
              <span className="hidden sm:inline">New Project</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>

          {/* Projects Grid */}
          {projects.length === 0 ? (
            <div className="flex items-center justify-center min-h-[50vh] px-4">
              <div className="text-center max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div 
                  className="premium-white-glass rounded-3xl p-8 md:p-10"
                  style={{ 
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.6)',
                  }}
                >
                  <FolderKanban 
                    className="w-16 h-16 mx-auto mb-6" 
                    style={{ color: textMutedColor }}
                  />
                  <h2 
                    className="text-2xl md:text-3xl font-semibold mb-3"
                    style={{ color: textColor }}
                  >
                    No projects yet
                  </h2>
                  <p 
                    className="text-base md:text-lg mb-6"
                    style={{ color: textSecondaryColor }}
                  >
                    Create your first project to get started with organized workspaces
                  </p>
                  <button
                    onClick={() => router.push("/projects/new")}
                    className="px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
                    style={{
                      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                      color: getTextColorForBackground(primaryColor),
                      boxShadow: `0 4px 16px ${primaryColor}40`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = `0 6px 24px ${primaryColor}60`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = `0 4px 16px ${primaryColor}40`;
                    }}
                  >
                    Create your first project
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => router.push(`/projects/${project.id}`)}
                  className="premium-white-glass rounded-2xl p-5 md:p-6 transition-all duration-200 text-left group hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    boxShadow: '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = `0 8px 32px ${primaryColor}20, inset 0 1px 0 rgba(255,255,255,0.6)`;
                    e.currentTarget.style.borderColor = `${primaryColor}40`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                  }}
                >
                  <div 
                    className="font-semibold text-lg md:text-xl mb-2 line-clamp-2"
                    style={{ color: textColor }}
                  >
                    {project.name}
                  </div>
                  {project.description && (
                    <div 
                      className="text-sm md:text-base mb-4 line-clamp-3 leading-relaxed"
                      style={{ color: textMutedColor }}
                    >
                      {project.description}
                    </div>
                  )}
                  <div 
                    className="flex items-center gap-3 text-xs mt-4 pt-4 border-t"
                    style={{ 
                      color: textVeryMutedColor,
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <span 
                      className="capitalize px-2 py-1 rounded-lg font-medium"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}15 0%, ${secondaryColor}15 100%)`,
                        color: primaryColor,
                      }}
                    >
                      {project.privacyMode}
                    </span>
                    <span>{new Date(project.updatedAt).toLocaleDateString()}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Premium Theme-Aware Navigation */}
      <PremiumNavigation 
        profile={profile} 
        hideOnPaths={[]}
        config={{
          style: "island",
          position: "bottom",
        }}
      />
    </div>
  );
}

